import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  moodEventHref,
  readRefreshCompareVerdict,
  readRefreshPublishedSummary,
  refreshInstantDisplay,
  refreshPageGoneReasonHint,
  refreshPageGoneReasonLabel,
  refreshProblemOf,
} from "@/lib/tina/refresh-jobs";
import {
  KeyValueGrid,
  type KeyValueRow,
} from "@/pages/tina/items/components/key-value-grid";
import { percent } from "@/pages/tina/items/components/panels/hitl-panel/refresh-resolution.helpers";
import type { HitlContext } from "@/types/tina";

/**
 * What the curator reads before deciding, for a `refresh_review` park.
 *
 * Rendered from `HitlContextView`'s branch, above the actions — the same slot and the
 * same job as `EnrichmentContextView`, and it exists for the same reason: the three
 * scrape branches (music/cinema/theater) key off stage outputs a refresh job does not
 * have, so a refresh park fell through to "No structured HITL context available for this
 * content type." above a generic one-liner about source pages disagreeing. Everything
 * concrete on the screen was in the decision form below it, and one thing was on no
 * screen at all.
 *
 * **That one thing is the model's reasoning.** `ProposedChange.evidence` — the verbatim
 * page quote the form already shows per proposal — answers "where does this value come
 * from". It does not answer "what did the model make of this page", which is
 * `stage_outputs.refresh_compare.reasoning` and was never rendered anywhere. On a
 * `page_gone` park it is the ONLY reasoning that exists: `proposals` is empty there by
 * design, so a curator deciding whether to cancel a published event and notify everyone
 * who saved it had a four-word code (`focal_missing`) and nothing else.
 *
 * **What this deliberately does not repeat.** The proposals themselves, their old→new
 * values, their confidences and their evidence all belong beside the Accept/Reject
 * buttons that act on them, and `RefreshResolution` renders them there. `HitlPanel` only
 * mounts at `status === "hitl"`, so that form is always on the screen under this — a
 * second read-only copy of the same cards would be two lists of the same changes. The
 * precedent is the panel's own: it already suppresses `HitlReasoningView` for `low_score`
 * because `LowScoreResolution` owns that disclosure.
 *
 * **Nor the source URL** (owner ruling R76). This used to carry a full-width "Source" row
 * printing the whole of `problem.source_url`, and `RefreshHeader` a "Source page" link to
 * the same string — on a screen whose own page header already links it. That is not two
 * near-copies but one: `refresh_apply` builds `source_url` from `events.url` verbatim
 * (`stages/apply.py`), and `itemSourceUrl()` reads that same column first, so the item
 * header's link and this row were always the identical URL. One survives, the page
 * header's, because it is where a curator looks for an item's source on every other Tina
 * item too.
 *
 * So this answers the two questions the form does not: **which published event is this**
 * (the item header identifies the row by Tina id, run and site — true for a scrape row,
 * useless for a job whose subject is a live event with a name), and **what did the model
 * conclude about the page as a whole**.
 */
export function RefreshContextView({
  hitlContext,
}: {
  hitlContext: HitlContext;
}) {
  const problem = refreshProblemOf(hitlContext);
  // The registry only routes `refresh_review` here, so this is a guard against a
  // mismatched context rather than a path a curator can reach.
  if (!problem) return null;

  const published = readRefreshPublishedSummary(hitlContext);
  const verdict = readRefreshCompareVerdict(hitlContext);
  const eventHref = moodEventHref(problem.admin_event_id);

  const rows: KeyValueRow[] = [
    {
      label: "Event",
      value: (
        <span className="inline-flex flex-wrap items-center gap-2">
          {/* `moodEventHref`, never `problem.ims_url`: tina stamps that as
              `…/events/show/{id}`, a route IMS declares but never mounts. */}
          {eventHref ? (
            <Link
              to={eventHref}
              className="font-medium text-blue-500 hover:underline"
            >
              {published?.name ?? problem.admin_event_id}
            </Link>
          ) : (
            <span className="font-medium">
              {published?.name ?? problem.admin_event_id}
            </span>
          )}
          {published?.status && (
            <Badge variant="secondary" className="font-normal">
              {published.status}
            </Badge>
          )}
        </span>
      ),
    },
  ];

  if (published?.start_date) {
    const start = refreshInstantDisplay(
      published.start_date,
      published.timezone,
    );
    rows.push({
      label: "Published",
      // On the event's own clock first, the stored string underneath — the shape
      // `DateComparison` already uses for a `date` proposal, so the two halves of
      // this panel answer "when is this event?" the same way.
      //
      // This row used to print the stored UTC string alone, beside the words
      // "event timezone America/Los_Angeles" — naming the zone and not applying
      // it. Its justification was that the form below printed `old`/`new` in
      // exactly this form, and R80 made that false: the form normalises and keeps
      // the raw strings as a secondary line. So the one screen a curator uses to
      // answer "did the date change?" showed 12 Sep where `event/edit` showed 11
      // Sep, for the same instant.
      value: (
        <div className="flex flex-col gap-0.5">
          {start.status === "normalised" ? (
            <span className="inline-flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{start.text}</span>
              <span className="text-xs text-muted-foreground">
                {start.frame}
              </span>
            </span>
          ) : (
            // Amber, as on an un-normalised proposal: a start that could not be
            // placed on a clock is a state the curator has to account for.
            <span className="text-xs text-amber-700 dark:text-amber-300">
              {start.note}
            </span>
          )}
          {/* Verbatim and never reformatted. It is what is stored on the Mood
              event, what a curator greps for, and what an offset bug shows up
              in — demoted, not dropped. */}
          <span className="inline-flex flex-wrap items-baseline gap-2 font-mono text-[11px] text-muted-foreground">
            {published.start_date}
            {start.status === "raw-only" && published.timezone && (
              <span className="font-sans">
                event timezone {published.timezone}
              </span>
            )}
          </span>
        </div>
      ),
    });
  }

  if (published?.venue_name) {
    rows.push({
      label: "Venue",
      value: [published.venue_name, published.venue_city]
        .filter(Boolean)
        .join(" · "),
    });
  }

  if (published && published.lineup_names.length > 0) {
    rows.push({ label: "Lineup", value: published.lineup_names.join(", ") });
  }

  const verdictRow = verdictValue(verdict);
  if (verdictRow) rows.push({ label: "Verdict", value: verdictRow });

  if (problem.page_gone) {
    const label = refreshPageGoneReasonLabel(problem.page_gone_reason);
    if (label) {
      rows.push({
        label: "Page gone",
        // Raw code on hover — it is what a curator greps tina's logs for.
        value: (
          <span title={problem.page_gone_reason ?? undefined}>
            {label}
            {refreshPageGoneReasonHint(problem.page_gone_reason) && (
              <span className="block text-xs text-muted-foreground">
                {refreshPageGoneReasonHint(problem.page_gone_reason)}
              </span>
            )}
          </span>
        ),
      });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Refresh context
      </h3>
      <KeyValueGrid rows={rows} />
      {verdict?.reasoning && (
        <div className="flex flex-col gap-1 rounded-md border bg-muted/20 p-3">
          <h4 className="text-xs uppercase tracking-wide text-muted-foreground">
            Model reasoning
          </h4>
          {/* Whole, wrapped, never clamped. It is a couple of sentences at most,
              and it is the thing a curator is here to read — a truncated
              explanation of why an event should be cancelled is worse than none. */}
          <p className="whitespace-pre-wrap text-sm">{verdict.reasoning}</p>
        </div>
      )}
    </div>
  );
}

/**
 * The compare model's headline call on the page, or null when the park records none.
 *
 * `same_event` is the switch the whole pipeline turns on: `page_gone` is exactly
 * `mode === "scheduled" && !same_event`, and `focal_instance_found` is what splits a
 * `focal_missing` park from a `same_event_false` one. Spelled out here because those
 * three booleans are the difference between "this tour page has moved on to the next
 * date" and "this event is gone", and the curator's answer differs.
 */
function verdictValue(verdict: ReturnType<typeof readRefreshCompareVerdict>) {
  if (!verdict || verdict.same_event === null) return null;

  const notes: string[] = [];
  if (verdict.focal_instance_found === false) {
    notes.push("the published date was not on the page");
  }
  if (verdict.other_instances_on_page === true) {
    notes.push("other dates for the same act were");
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Badge
        className={
          verdict.same_event
            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
            : "bg-rose-500/15 text-rose-700 dark:text-rose-300"
        }
      >
        {verdict.same_event ? "Still the same event" : "No longer this event"}
      </Badge>
      {typeof verdict.same_event_confidence === "number" && (
        <span className="text-xs text-muted-foreground">
          {percent(verdict.same_event_confidence)} confident
        </span>
      )}
      {notes.length > 0 && (
        <span className="text-xs text-muted-foreground">
          {notes.join(", ")}
        </span>
      )}
    </span>
  );
}
